import React, { useState, useEffect, useCallback } from 'react';
import { FaPencilAlt, FaMinusCircle, FaPlus, FaTimes, FaCheckCircle, FaTags } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { FacilityType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const FacilityTypes = () => {
        const { token } = useAuth();
        const [facilityTypes, setFacilityTypes] = useState<FacilityType[]>([]);
        const [loading, setLoading] = useState<boolean>(true);
        const [error, setError] = useState<string | null>(null);
        const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
        const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
        const [newTypeName, setNewTypeName] = useState<string>('');
        const [editingType, setEditingType] = useState<FacilityType | null>(null);
        const [formData, setFormData] = useState({
                name: '',
                description: '',
                color_code: '#000000'
        });

        // Load facility types
        const loadFacilityTypes = useCallback(async () => {
                try {
                        setLoading(true);
                        setError(null);
                        const response = await apiCall('/facilities/facility-types/', { method: 'GET' }, token || undefined);
                        const data = await response.json();
                        console.log('Facility Types API Response:', data);
                        // Handle paginated response
                        const items = data.results || data;
                        setFacilityTypes(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading facility types:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load');
                } finally {
                        setLoading(false);
                }
        }, [token]);

        useEffect(() => {
                loadFacilityTypes();
        }, [loadFacilityTypes]);

        const handleCreate = async (e: React.FormEvent) => {
                e.preventDefault();
                try {
                        setError(null);
                        await apiCall('/facilities/facility-types/', {
                                method: 'POST',
                                body: JSON.stringify({
                                        name: newTypeName,
                                        description: formData.description,
                                        color_code: formData.color_code,
                                }),
                        }, token || undefined);
                        setNewTypeName('');
                        setFormData({ name: '', description: '', color_code: '#000000' });
                        setIsCreateModalOpen(false);
                        loadFacilityTypes();
                } catch (err) {
                        console.error('Error creating facility type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to create');
                }
        };

        const handleEdit = async (e: React.FormEvent) => {
                e.preventDefault();
                if (!editingType) return;
                try {
                        setError(null);
                        console.log('Sending facility type update:', formData);
                        const response = await apiCall(`/facilities/facility-types/${editingType.id}/`, {
                                method: 'PATCH',
                                body: JSON.stringify(formData),
                        }, token || undefined);
                        const updated = await response.json();
                        console.log('Received updated facility type:', updated);
                        setIsEditModalOpen(false);
                        setEditingType(null);
                        loadFacilityTypes();
                } catch (err) {
                        console.error('Error updating facility type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to update');
                }
        };

        const handleMarkInactive = async (typeId: number) => {
                if (!window.confirm('Are you sure you want to deactivate this facility type?')) {
                        return;
                }
                try {
                        setError(null);
                        await apiCall(`/facilities/facility-types/${typeId}/`, {
                                method: 'PATCH',
                                body: JSON.stringify({ is_active: false }),
                        }, token || undefined);
                        loadFacilityTypes();
                } catch (err) {
                        console.error('Error deactivating facility type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to deactivate');
                }
        };

        const handleReactivate = async (typeId: number) => {
                if (!window.confirm('Are you sure you want to reactivate this facility type?')) {
                        return;
                }
                try {
                        setError(null);
                        await apiCall(`/facilities/facility-types/${typeId}/`, {
                                method: 'PATCH',
                                body: JSON.stringify({ is_active: true }),
                        }, token || undefined);
                        loadFacilityTypes();
                } catch (err) {
                        console.error('Error reactivating facility type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to reactivate');
                }
        };

        const openEditModal = (type: FacilityType) => {
                console.log('Opening edit modal for facility type:', type);
                setEditingType(type);
                setFormData({ name: type.name, description: type.description || '', color_code: type.color_code || '#000000' });
                setIsEditModalOpen(true);
        };

        return (
                <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaTags className="text-primary" />
                                        Facility Types
                                </h1>
                                <button
                                        className="btn btn-primary"
                                        onClick={() => setIsCreateModalOpen(true)}
                                >
                                        <FaPlus className="mr-2" /> Create Facility Type
                                </button>
                        </div>

                        {error && (
                                <div className="alert alert-error mb-4">
                                        <span>{error}</span>
                                </div>
                        )}

                        {loading ? (
                                <div className="flex justify-center items-center py-12">
                                        <div className="loading loading-spinner loading-lg"></div>
                                </div>
                        ) : facilityTypes.length === 0 ? (
                                <div className="text-center py-12">
                                        <p className="text-base-content opacity-60">No facility types found.</p>
                                        <button
                                                className="btn btn-primary mt-4"
                                                onClick={() => setIsCreateModalOpen(true)}
                                        >
                                                <FaPlus className="mr-2" /> Create Your First Facility Type
                                        </button>
                                </div>
                        ) : (
                                <div className="relative overflow-x-auto rounded-lg">
                                        <table className="table table-zebra bg-base-100/50">
                                                <thead>
                                                        <tr>
                                                                <th>Facility Type Name</th>
                                                                <th className="text-center">Colour Code</th>
                                                                <th>Status</th>
                                                                <th className="w-24">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        {facilityTypes.map((type) => (
                                                                <tr key={type.id}>
                                                                        <td>{type.name}</td>
                                                                        <td>
                                                                                <div className="flex justify-center items-center">
                                                                                        <div 
                                                                                                className="w-6 h-6 rounded border border-base-300"
                                                                                                style={{ backgroundColor: type.color_code || '#808080' }}
                                                                                                title={type.color_code || 'No colour set'}
                                                                                        />
                                                                                </div>
                                                                        </td>
                                                                        <td>
                                                                                <span className={`badge ${type.is_active ? 'badge-success' : 'badge-error'}`}>
                                                                                        {type.is_active ? 'Active' : 'Inactive'}
                                                                                </span>
                                                                        </td>
                                                                        <td>
                                                                                <div className="flex gap-2">
                                                                                        <button
                                                                                                className="btn btn-ghost btn-xs"
                                                                                                onClick={() => openEditModal(type)}
                                                                                                title="Edit"
                                                                                        >
                                                                                                <FaPencilAlt className="w-4 h-4" />
                                                                                        </button>
                                                                                        {type.is_active ? (
                                                                                                <button
                                                                                                        className="btn btn-ghost btn-xs text-error"
                                                                                                        onClick={() => handleMarkInactive(type.id)}
                                                                                                        title="Deactivate"
                                                                                                >
                                                                                                        <FaMinusCircle className="w-4 h-4" />
                                                                                                </button>
                                                                                        ) : (
                                                                                                <button
                                                                                                        className="btn btn-ghost btn-xs text-success"
                                                                                                        onClick={() => handleReactivate(type.id)}
                                                                                                        title="Reactivate"
                                                                                                >
                                                                                                        <FaCheckCircle className="w-4 h-4" />
                                                                                                </button>
                                                                                        )}
                                                                                </div>
                                                                        </td>
                                                                </tr>
                                                        ))}
                                                </tbody>
                                        </table>
                                </div>
                        )}

                        {/* Create Facility Type Modal */}
                        {isCreateModalOpen && (
                                <div className="modal modal-open">
                                        <div className="modal-box">
                                                <div className="flex justify-between items-center mb-4">
                                                        <h3 className="font-bold text-lg">Create New Facility Type</h3>
                                                        <button
                                                                className="btn btn-sm btn-circle btn-ghost"
                                                                onClick={() => setIsCreateModalOpen(false)}
                                                        >
                                                                <FaTimes />
                                                        </button>
                                                </div>
                                                <form onSubmit={handleCreate}>
                                                        <div className="form-control mb-4">
                                                                <label className="label">
                                                                        <span className="label-text">Facility Type Name *</span>
                                                                </label>
                                                                <input
                                                                        type="text"
                                                                        placeholder="e.g., Consulting Suite"
                                                                        className="input input-bordered"
                                                                        value={newTypeName}
                                                                        onChange={(e) => setNewTypeName(e.target.value)}
                                                                        required
                                                                />
                                                        </div>
                                                        <div className="form-control mb-4">
                                                                <label className="label">
                                                                        <span className="label-text">Description</span>
                                                                </label>
                                                                <textarea
                                                                        placeholder="Optional description"
                                                                        className="textarea textarea-bordered"
                                                                        value={formData.description}
                                                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                                        rows={3}
                                                                />
                                                        </div>
                                                        <div className="form-control mb-4">
                                                                <label className="label">
                                                                        <span className="label-text">Colour Code</span>
                                                                </label>
                                                                <div className="relative inline-block">
                                                                        <input
                                                                                type="color"
                                                                                name="color_code"
                                                                                value={formData.color_code}
                                                                                onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                                                                                className="absolute inset-0 w-6 h-6 opacity-0 cursor-pointer"
                                                                        />
                                                                        <div 
                                                                                className="w-6 h-6 rounded border border-base-300 hover:border-primary transition-colors pointer-events-none"
                                                                                style={{ backgroundColor: formData.color_code || '#000000' }}
                                                                                title="Click to change colour"
                                                                        />
                                                                </div>
                                                        </div>
                                                        <div className="modal-action">
                                                                <button type="button" className="btn" onClick={() => setIsCreateModalOpen(false)}>
                                                                        Cancel
                                                                </button>
                                                                <button type="submit" className="btn btn-primary">Create</button>
                                                        </div>
                                                </form>
                                        </div>
                                </div>
                        )}

                        {/* Edit Facility Type Modal */}
                        {isEditModalOpen && editingType && (
                                <div className="modal modal-open">
                                        <div className="modal-box">
                                                <div className="flex justify-between items-center mb-4">
                                                        <h3 className="font-bold text-lg">Edit Facility Type</h3>
                                                        <button
                                                                className="btn btn-sm btn-circle btn-ghost"
                                                                onClick={() => {
                                                                        setIsEditModalOpen(false);
                                                                        setEditingType(null);
                                                                }}
                                                        >
                                                                <FaTimes />
                                                        </button>
                                                        </div>
                                                <form onSubmit={handleEdit}>
                                                        <div className="form-control mb-4">
                                                                <label className="label">
                                                                        <span className="label-text">Facility Type Name *</span>
                                                                </label>
                                                                <input
                                                                        type="text"
                                                                        placeholder="e.g., Consulting Suite"
                                                                        className="input input-bordered"
                                                                        value={formData.name}
                                                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                                        required
                                                                />
                                                        </div>
                                                        <div className="form-control mb-4">
                                                                <label className="label">
                                                                        <span className="label-text">Description</span>
                                                                </label>
                                                                <textarea
                                                                        placeholder="Optional description"
                                                                        className="textarea textarea-bordered"
                                                                        value={formData.description}
                                                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                                        rows={3}
                                                                />
                                                        </div>
                                                        <div className="form-control mb-4">
                                                                <label className="label">
                                                                        <span className="label-text">Colour Code</span>
                                                                </label>
                                                                <div className="relative inline-block">
                                                                        <input
                                                                                type="color"
                                                                                name="color_code"
                                                                                value={formData.color_code}
                                                                                onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                                                                                className="absolute inset-0 w-6 h-6 opacity-0 cursor-pointer"
                                                                        />
                                                                        <div 
                                                                                className="w-6 h-6 rounded border border-base-300 hover:border-primary transition-colors pointer-events-none"
                                                                                style={{ backgroundColor: formData.color_code || '#000000' }}
                                                                                title="Click to change colour"
                                                                        />
                                                                </div>
                                                        </div>
                                                        <div className="modal-action">
                                                                <button
                                                                        type="button"
                                                                        className="btn"
                                                                        onClick={() => {
                                                                                setIsEditModalOpen(false);
                                                                                setEditingType(null);
                                                                        }}
                                                                >
                                                                        Cancel
                                                                </button>
                                                                <button type="submit" className="btn btn-primary">Save Changes</button>
                                                        </div>
                                                </form>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default FacilityTypes;